import { Link } from 'react-router-dom';
import { Stack, Text, Group, Avatar, Badge, Button, Skeleton, Anchor, Divider } from '@mantine/core';
import { IconUser } from '@tabler/icons-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { usersService } from '../../services/users';
import { useAuth } from '../../context/AuthContext';
import { SuggestedUsers } from './SuggestedUsers';

interface Props {
  username: string;
}

export function UserProfileSidebar({ username }: Props) {
  const { user } = useAuth();
  const [followed, setFollowed] = useState<Set<string>>(new Set());

  const { data: similar, isLoading } = useQuery({
    queryKey: ['similar-reviewers', username],
    queryFn: () => usersService.getSimilarReviewers(username, 3),
    staleTime: 30 * 60 * 1000,
    enabled: !!username,
  });

  const { data: topTags, isLoading: loadingTags } = useQuery({
    queryKey: ['user-top-tags', username],
    queryFn: () => usersService.getTopTags(username, 5),
    staleTime: 30 * 60 * 1000,
    enabled: !!username,
  });

  const followMutation = useMutation({
    mutationFn: (id: string) => usersService.follow(id),
    onSuccess: (_, id) => setFollowed((prev) => new Set(prev).add(id)),
  });

  const visibleSimilar = (similar ?? []).filter(
    (r) => r.id !== user?.id && !followed.has(r.id)
  );

  return (
    <Stack gap="lg">
      {/* Similar reviewers */}
      {(isLoading || visibleSimilar.length > 0) && (
        <Stack gap="sm">
          <Text size="sm" fw={600}>Similar Reviewers</Text>
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Group key={i} justify="space-between">
                <Group gap="sm"><Skeleton circle h={32} /><Skeleton h={12} w={80} /></Group>
                <Skeleton h={28} w={60} radius="xl" />
              </Group>
            ))
          ) : (
            visibleSimilar.map((r) => (
              <Group key={r.id} justify="space-between" wrap="nowrap">
                <Anchor component={Link} to={`/users/${r.username}`} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap="sm" wrap="nowrap">
                    <Avatar src={r.profileImageUrl} size="sm" radius="xl"><IconUser size={14} /></Avatar>
                    <Stack gap={0} style={{ minWidth: 0 }}>
                      <Text size="sm" fw={500} lineClamp={1}>@{r.username}</Text>
                      <Text size="xs" c="dimmed">{r.totalReviews} reviews</Text>
                    </Stack>
                  </Group>
                </Anchor>
                <Button size="xs" radius="xl" variant="outline"
                  loading={followMutation.isPending && followMutation.variables === r.id}
                  onClick={() => followMutation.mutate(r.id)}>
                  Follow
                </Button>
              </Group>
            ))
          )}
        </Stack>
      )}

      {/* Most-Used Tags */}
      {(loadingTags || (topTags ?? []).length > 0) && (
        <>
          <Divider />
          <Stack gap="sm">
            <Text size="sm" fw={600}>Their Favourite Tags</Text>
            {loadingTags ? (
              <Group gap={6} wrap="wrap">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} h={24} w={70} radius="xl" />
                ))}
              </Group>
            ) : (
              <Group gap={6} wrap="wrap">
                {(topTags ?? []).map((tag) => (
                  <Badge
                    key={tag.id}
                    component={Link}
                    to={`/tags/${tag.slug}`}
                    variant="light"
                    radius="xl"
                    size="sm"
                    style={{ cursor: 'pointer', textDecoration: 'none' }}
                  >
                    #{tag.name}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>
        </>
      )}

      {/* Global who-to-follow fallback */}
      <Divider />
      <SuggestedUsers />

      <Divider />
      <Stack gap={4}>
        <Text size="xs" c="dimmed">
          <Anchor component={Link} to="/privacy" size="xs" c="dimmed">Privacy</Anchor>
          {' · '}
          <Anchor component={Link} to="/terms" size="xs" c="dimmed">Terms</Anchor>
        </Text>
        <Text size="xs" c="dimmed">© {new Date().getFullYear()} CartCritic</Text>
      </Stack>
    </Stack>
  );
}
